import { useNode } from "@craftjs/core";
import * as React from "react";
import { useTranslation } from "react-i18next";

import ImagePositioner from "@/components/editor/image-positioner/image-positioner-component";
import ImageUpload from "@/components/editor/image-upload/image-upload-component";
import SettingsTitleWrapper from "@/components/editor/shared/settings-title-wrapper";
import { Input, Select, SwitchInput } from "@/components/shared/forms";
import LogoResize from "@/components/shared/logo-resize";
import { IMAGE_UPLOAD_TYPES } from "@/features/analytics";

export type HeaderSettingsProps = {
  title?: string;
  optimalImageSize?: string;
  disableBackgroundImageAlt?: boolean;
  backgroundImageSize?: {
    width: string;
    height: string;
  };
};

export type ImageFit = "cover" | "contain";
type ImageFitOption = { label: string; value: ImageFit };

const MIN_LOGO_SIZE = 10;
const MAX_LOGO_SIZE = 100;
const FALLBACK_LOGO_SIZE = 100;

export default function HeaderSettings({
  title,
  optimalImageSize = "1440×800px",
  backgroundImageSize = {
    width: "288",
    height: "160",
  },
  disableBackgroundImageAlt,
}: HeaderSettingsProps) {
  const {
    props,
    actions: { setProp },
    nodeId,
  } = useNode((node) => {
    return {
      nodeId: node.id,
      props: node.data.props,
    };
  });

  const { t } = useTranslation();

  const handleToggle = React.useCallback(() => {
    setProp((props) => (props.isLogoShowing = !props.isLogoShowing));
  }, [setProp]);

  const handleLogoSizeChange = React.useCallback(
    (size: number) => {
      setProp((props) => (props.logoSize = size));
    },
    [setProp]
  );

  const setImageFit = React.useCallback(
    (value) => {
      setProp((props) => (props.imageFit = value));
    },
    [setProp]
  );

  const imageFitOptions = React.useMemo<ImageFitOption[]>(
    () => [
      { label: t("preference-panel.image-dynamic-scaling"), value: "cover" },
      { label: t("preference-panel.image-full-width"), value: "contain" },
    ],
    [t]
  );

  return (
    <React.Fragment>
      <SettingsTitleWrapper title={title || t("preference-panel.header")}>
        <div className="grid gap-4">
          <p className="text-xs font-medium leading-4 text-gray-700">
            {t("preference-panel.background-image")}
          </p>
          <div className="grid gap-2">
            <ImageUpload
              blockType={IMAGE_UPLOAD_TYPES.HEADER_BACKGROUND}
              nodeId={nodeId}
              imageWidth={backgroundImageSize.width}
              imageHeight={backgroundImageSize.height}
              imagePropName="backgroundImage"
            />
            <p className="text-sm leading-6 text-gray-600">
              {t("preference-panel.please-use-optimal-image", {
                imageSize: optimalImageSize,
              })}
            </p>
            {props.backgroundImage && (
              <div className="mb-4">
                <Select
                  name="image-fit"
                  label={t("preference-panel.image-fit")}
                  options={imageFitOptions}
                  value={props.imageFit || "cover"}
                  onChange={setImageFit}
                  dataTestId="image-fit-select"
                />
                {!disableBackgroundImageAlt && (
                  <Input
                    className="mt-4"
                    name="img-alt"
                    label={t("preference-panel.image-alt-text")}
                    value={props.backgroundImageAlt || ""}
                    onChange={(e) => {
                      setProp((props) => {
                        props.backgroundImageAlt = e.currentTarget.value;
                      });
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </SettingsTitleWrapper>
      <SettingsTitleWrapper title={t("preference-panel.image-position")}>
        <div className="mb-6">
          <ImagePositioner nodeId={nodeId} imagePropName="backgroundPosition" />
        </div>
      </SettingsTitleWrapper>
      <SettingsTitleWrapper title={t("preference-panel.logo")}>
        <div className="grid min-w-0 gap-4">
          <p className="text-xs font-medium leading-4 text-gray-700">
            {t("preference-panel.logo-image")}
          </p>
          <div className="grid min-w-0 gap-2">
            <div className="relative">
              <ImageUpload
                blockType={IMAGE_UPLOAD_TYPES.HEADER_LOGO}
                nodeId={nodeId}
                imagePropName="logo"
              />
              <div className="mt-4">
                <SwitchInput
                  label={t("preference-panel.show-in-header")}
                  onChange={handleToggle}
                  checked={props.isLogoShowing}
                  data-testid="toggle-logo-button"
                />
              </div>
            </div>
            <p className="mb-4 text-sm leading-6 text-gray-600">
              {t("preference-panel.please-use-transparent-background")}
            </p>
            {props.isLogoShowing && (
              <div className="mb-3 min-w-0" data-testid="logo-resizer">
                <p className="mb-4 text-xs font-medium text-gray-700">
                  {t("preference-panel.logo-size")}
                </p>
                <LogoResize
                  logoSize={props.logoSize ?? FALLBACK_LOGO_SIZE}
                  minLogoSize={MIN_LOGO_SIZE}
                  maxLogoSize={MAX_LOGO_SIZE}
                  onLogoSizeChange={handleLogoSizeChange}
                  label={t("preference-panel.logo-size")}
                />
              </div>
            )}
          </div>
        </div>
      </SettingsTitleWrapper>
    </React.Fragment>
  );
}
